import { useT } from '../i18n';
import './TestBar.css';

// A static, scannable "test bar" rendered as inline SVG.
// - Treble clef, key signature G major (one sharp on F),
//   four quarter notes G4 → B4 → D5 → G5 on the staff.
// - All notes are naturals in G major, so they also sound right if the OMR
//   pipeline reads the key as C major (which the current stub does).
// - Printed to sit ~4–6 cm wide on a typical desktop monitor, giving a phone
//   camera a reasonable frame when pointed at the screen.

interface NoteSpec {
  x: number;
  y: number;
  stemDir: 'up' | 'down';
}

const STAFF_LINES = [12, 20, 28, 36, 44];
const LEFT = 6;
const RIGHT = 314;

const NOTES: NoteSpec[] = [
  { x: 110, y: 36, stemDir: 'up' },   // G4
  { x: 165, y: 28, stemDir: 'down' }, // B4
  { x: 220, y: 20, stemDir: 'down' }, // D5
  { x: 275, y: 8, stemDir: 'down' },  // G5
];

const STEM_LEN = 22;

export function TestBar() {
  const { t } = useT();
  return (
    <figure className="testbar" aria-label={t('landing.testBarAria')}>
      <svg
        viewBox="0 0 320 56"
        className="testbar__svg"
        role="img"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Staff lines */}
        {STAFF_LINES.map((y) => (
          <line key={y} x1={LEFT} y1={y} x2={RIGHT} y2={y} stroke="#1f1f1f" strokeWidth="1.1" />
        ))}
        {/* Opening barline */}
        <line x1={LEFT} y1={STAFF_LINES[0]!} x2={LEFT} y2={STAFF_LINES[4]!} stroke="#1f1f1f" strokeWidth="1.1" />
        {/* Treble clef */}
        <text
          x="12"
          y="48"
          fontSize="48"
          fill="#1f1f1f"
          fontFamily="'Bravura','Noto Music','Segoe UI Symbol','Apple Symbols',serif"
        >
          𝄞
        </text>
        {/* Key signature — one sharp on F (top line, y=12) */}
        <text
          x="54"
          y="20"
          fontSize="26"
          fill="#1f1f1f"
          fontFamily="'Bravura','Noto Music','Segoe UI Symbol','Apple Symbols',serif"
        >
          ♯
        </text>
        {/* Noteheads + stems */}
        {NOTES.map((n, i) => {
          const stemX = n.stemDir === 'up' ? n.x + 5 : n.x - 5;
          const stemY2 = n.stemDir === 'up' ? n.y - STEM_LEN : n.y + STEM_LEN;
          return (
            <g key={i}>
              <ellipse
                cx={n.x}
                cy={n.y}
                rx="6"
                ry="4.2"
                fill="#1f1f1f"
                transform={`rotate(-25 ${n.x} ${n.y})`}
              />
              <line x1={stemX} y1={n.y} x2={stemX} y2={stemY2} stroke="#1f1f1f" strokeWidth="1.8" strokeLinecap="round" />
            </g>
          );
        })}
        {/* Final barline */}
        <line x1={RIGHT} y1={STAFF_LINES[0]!} x2={RIGHT} y2={STAFF_LINES[4]!} stroke="#1f1f1f" strokeWidth="2.2" />
      </svg>
      <figcaption className="testbar__caption">
        <span className="testbar__title">{t('landing.testBarTitle')}</span>
        <span className="testbar__sep">·</span>
        <span className="testbar__body">{t('landing.testBarCaption')}</span>
      </figcaption>
    </figure>
  );
}
